import {
  faClone,
  faPowerOff,
  faTrashCan,
} from "@fortawesome/pro-regular-svg-icons";
import { createColumnHelper } from "@tanstack/react-table";
import cronstrue from "cronstrue";
import { useNavigate } from "react-router-dom";

import { EllipsisOptionsDropdown } from "src/base-components/OptionsDropdown/EllipsisOptionsDropdown";
import { MENU_DIVIDER } from "src/base-components/OptionsDropdown/OptionsDropdownItems";
import { TableComp as Table } from "src/base-components/Table";
import { useModal } from "src/design-system/Modal";
import { Pill } from "src/design-system/Pill";
import { ExcludesFalse } from "src/flow/types";
import { useCapabilities } from "src/hooks/useCapabilities";
import { jobScope } from "src/hooks/useCapabilities/jobScope";
import { ActivateJobModal } from "src/jobs/common/ActivateJobModal";
import { DeactivateJobModal } from "src/jobs/common/DeactivateJobModal";
import { DeleteJobModal } from "src/jobs/common/DeleteJobModal";
import { JobStatusPill } from "src/jobs/common/JobStatusPill";
import { JobVersionsPills } from "src/jobs/common/JobVersionsPills";
import { ManageJobModal } from "src/jobs/common/ManageJobModal";
import { CellWrapper, ColumnHeader } from "src/jobs/common/TableElements";
import { getFutureMatches, getSourcePillIcon } from "src/jobs/jobUtils";
import { Job, JobWithVersionsUsed } from "src/jobs/types";
import { useFlowContext } from "src/router/routerContextHooks";
import { FlowPageParams, getUrlToJobPage } from "src/router/urls";
import { dateFromNow, formatDateWithLocalTimezone } from "src/utils/datetime";
import { getLocalTimezone, getTimezoneAbbreviation } from "src/utils/timezones";
import { useParamsDecode } from "src/utils/useParamsDecode";

const helper = createColumnHelper<JobWithVersionsUsed>();

const COLUMNS = [
  helper.accessor("name", {
    header: () => <ColumnHeader>Job name</ColumnHeader>,
    cell: (info) => <CellWrapper>{info.getValue()}</CellWrapper>,
  }),
  helper.accessor("status", {
    header: () => <ColumnHeader>Status</ColumnHeader>,
    cell: (info) => (
      <CellWrapper>
        <JobStatusPill status={info.getValue()} />
      </CellWrapper>
    ),
  }),
  helper.accessor("versionsUsed", {
    header: () => <ColumnHeader>Version</ColumnHeader>,
    cell: (info) => {
      const versionsUsed = info.getValue();
      return (
        <CellWrapper>
          <JobVersionsPills versionsUsed={versionsUsed} />
        </CellWrapper>
      );
    },
  }),
  helper.accessor("active_source", {
    header: () => <ColumnHeader>Data source</ColumnHeader>,
    cell: (info) => {
      const activeSource = info.getValue();

      if (!activeSource) return <CellWrapper>-</CellWrapper>;
      return (
        <CellWrapper>
          <Pill variant="gray">
            <Pill.Icon
              icon={getSourcePillIcon(activeSource.configuration.type)}
            ></Pill.Icon>
            <Pill.Text>{activeSource.name}</Pill.Text>
          </Pill>
        </CellWrapper>
      );
    },
  }),
  helper.accessor((row) => row.live_runs_count + row.sandbox_runs_count, {
    id: "total_runs",
    header: () => <ColumnHeader>Total runs</ColumnHeader>,
    cell: (info) => (
      <CellWrapper>
        <span data-loc="total-runs-count">{info.getValue()}</span>
      </CellWrapper>
    ),
  }),
  helper.accessor("last_run_at", {
    header: () => <ColumnHeader>Last run</ColumnHeader>,
    cell: (info) => {
      const lastRunAt = info.getValue();

      return (
        <CellWrapper>{lastRunAt ? dateFromNow(lastRunAt) : "-"}</CellWrapper>
      );
    },
  }),
  helper.accessor("schedule", {
    id: "next_run",
    header: () => <ColumnHeader>Next scheduled run</ColumnHeader>,
    cell: (info) => {
      const schedule = info.getValue();
      const { schedule_timezone } = info.row.original;
      if (!schedule || !schedule_timezone) return <CellWrapper>-</CellWrapper>;
      const nextRun = getFutureMatches(schedule, schedule_timezone);
      return (
        <CellWrapper>
          {formatDateWithLocalTimezone(nextRun, "MMM d, yyyy 'at' h:mmaaa")}
        </CellWrapper>
      );
    },
  }),
  helper.accessor("schedule", {
    header: () => <ColumnHeader>Schedule</ColumnHeader>,
    cell: (info) => {
      const schedule = info.getValue();
      if (!schedule) return <CellWrapper>-</CellWrapper>;

      const { schedule_timezone } = info.row.original;

      return (
        <CellWrapper>
          {cronstrue.toString(schedule, {
            verbose: true,
          })}{" "}
          (
          {schedule_timezone
            ? getTimezoneAbbreviation(schedule_timezone)
            : getTimezoneAbbreviation(getLocalTimezone())}
          )
        </CellWrapper>
      );
    },
  }),
  helper.display({
    id: "actions",
    cell: ({ row, table }) => {
      if (!table.options.meta?.actions) return null;

      return (
        <JobActionsMenu
          job={row.original}
          onActivate={table.options.meta?.actions.onActivateJob}
          onDeactivate={table.options.meta?.actions.onDeactivateJob}
          onDelete={table.options.meta?.actions.onDeleteJob}
          onDuplicate={table.options.meta?.actions.onDuplicateJob}
        />
      );
    },
  }),
];

const JobActionsMenu: React.FC<{
  job: Job;
  onDelete: (job: Job) => void;
  onDuplicate: (job: Job) => void;
  onActivate: (job: Job) => void;
  onDeactivate: (job: Job) => void;
}> = ({ job, onDelete, onDuplicate, onActivate, onDeactivate }) => {
  const { jobs } = useCapabilities();
  const { flow } = useFlowContext();
  const scope = jobScope(flow, job);
  const options = [
    jobs.canToggleStatus(scope.folder) && {
      key: job.status === "inactive" ? "Activate Job" : "Deactivate Job",
      icon: faPowerOff,
      action: () =>
        job.status === "inactive" ? onActivate(job) : onDeactivate(job),
    },
    jobs.canDuplicate(scope.folder) && {
      key: "Duplicate Job",
      icon: faClone,
      action: () => onDuplicate(job),
    },
    jobs.canDelete(scope) && MENU_DIVIDER,
    jobs.canDelete(scope) && {
      key: "Delete Job",
      icon: faTrashCan,
      action: () => onDelete(job),
    },
  ].filter(Boolean as unknown as ExcludesFalse);

  if (options.length === 0) return null;

  return (
    <EllipsisOptionsDropdown
      buttonClassName="text-gray-300 w-5 invisible group-hover:visible data-[menu-open=true]:visible"
      dataLoc="job-actions"
      elements={options}
      ellipsisDirection="vertical"
      timeoutDuration={200}
    />
  );
};

export const JobsTable: React.FC<{
  data: JobWithVersionsUsed[];
  isLoading: boolean;
}> = ({ data, isLoading }) => {
  const navigate = useNavigate();
  const {
    isOpen: isDeleteModalOpen,
    openModal: openDeleteModal,
    closeModal: closeDeleteModal,
    data: jobToDelete,
  } = useModal<Job>();
  const {
    isOpen: isDuplicateModalOpen,
    openModal: openDuplicateModal,
    closeModal: closeDuplicateModal,
    data: jobToDuplicate,
  } = useModal<Job>();
  const {
    isOpen: isActivateModalOpen,
    data: jobToActivate,
    openModal: openActivateModal,
    closeModal: closeActivateModal,
  } = useModal<Job>();
  const {
    isOpen: isDeactivateModalOpen,
    data: jobToDeactivate,
    openModal: openDeactivateModal,
    closeModal: closeDeactivateModal,
  } = useModal<Job>();

  const { orgId, wsId, flow_id } = useParamsDecode<FlowPageParams>();
  return (
    <>
      <Table
        actions={{
          onDeleteJob: (job: Job) => openDeleteModal(job),
          onDuplicateJob: (job: Job) => openDuplicateModal(job),
          onActivateJob: (job: Job) => openActivateModal(job),
          onDeactivateJob: (job: Job) => openDeactivateModal(job),
        }}
        columns={COLUMNS}
        data={data}
        frameClassName="w-full px-3 pb-2"
        isLoading={isLoading}
        rowClassName="cursor-pointer hover:bg-gray-50"
        rowPropsGetter={(row) => ({
          onClick: () =>
            navigate(getUrlToJobPage(orgId, wsId, flow_id, row.original.id)),
        })}
      />
      <DeleteJobModal
        isOpen={isDeleteModalOpen}
        job={jobToDelete}
        onClose={closeDeleteModal}
      />
      <ManageJobModal
        action="duplicate"
        job={jobToDuplicate}
        open={isDuplicateModalOpen}
        onClose={closeDuplicateModal}
      />
      <ActivateJobModal
        isOpen={isActivateModalOpen}
        job={jobToActivate}
        onClose={closeActivateModal}
      />
      <DeactivateJobModal
        isOpen={isDeactivateModalOpen}
        job={jobToDeactivate}
        onClose={closeDeactivateModal}
      />
    </>
  );
};
